import { EditorState } from "draft-js";
import { convertFromHTML } from 'draft-convert';
import decorator from "./decorator";

const convertToRGB = (hex: string) => {
    const r = "0x" + hex[1] + hex[2];
    const g = "0x" + hex[3] + hex[4];
    const b = "0x" + hex[5] + hex[6];
    return `rgb(${+r}, ${+g}, ${+b})`;
};



const parseStyleString = (styleString: string | null): Record<string, string> => {
    const parsedStyles: Record<string, string> = {};
    if (!styleString) {
        return parsedStyles;
    }

    styleString.split(';').forEach(declaration => {
        const trimmedDeclaration = declaration.trim();
        if (trimmedDeclaration) {
            const parts = trimmedDeclaration.split(':');
            if (parts.length >= 2) {
                const prop = parts[0].trim();
                const value = parts.slice(1).join(':').trim();
                parsedStyles[prop] = value;
            }
        }
    });
    return parsedStyles;
};

const kebabToCamelCase = (str: string): string => {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

// Annule le &nbsp; injecté par renderRichEditorContentHtml.tsx dans les <p> vides (pour l'affichage mail), sinon la ligne vide restaurée n'est plus vraiment vide et nécessite un backspace en trop pour être supprimée.
const removeStandaloneNbspFromEmptyParagraphs = (html: string): string => {
    const container = document.implementation.createHTMLDocument().body;
    container.innerHTML = html;

    container.querySelectorAll('p').forEach((paragraph) => {
        if (
            paragraph.children.length === 0 &&
            paragraph.textContent?.replace(/\u00A0/g, ' ').trim() === ''
        ) {
            paragraph.innerHTML = '';
        }
    });

    return container.innerHTML;
};

// Une serie de 2+ <br> consecutifs dans un meme bloc (plusieurs lignes vides tapees au clavier avec
// Shift+Entree) rend Draft.js instable : taper du texte a proximite peut dupliquer des retours a la
// ligne. On eclate ces series en paragraphes vides separes, que Draft.js gere de facon fiable
// (suppression en un seul backspace, pas de corruption a la frappe).
const BLOCK_TAGS_WITH_LINE_BREAKS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote'];

const splitConsecutiveLineBreaksIntoParagraphs = (html: string): string => {
    const container = document.implementation.createHTMLDocument().body;
    container.innerHTML = html;

    container.querySelectorAll(BLOCK_TAGS_WITH_LINE_BREAKS.join(',')).forEach((block) => {
        const segments: ChildNode[][] = [[]];
        let consecutiveBr = 0;

        Array.from(block.childNodes).forEach((node) => {
            if (node.nodeName.toLowerCase() === 'br') {
                consecutiveBr += 1;
                if (consecutiveBr === 1) {
                    segments[segments.length - 1].push(node);
                } else {
                    segments.push([]);
                }
            } else {
                consecutiveBr = 0;
                segments[segments.length - 1].push(node);
            }
        });

        if (segments.length <= 1) {
            return;
        }

        segments.forEach((segmentNodes) => {
            const newBlock = document.createElement(block.tagName);
            if (block.getAttribute('style')) {
                newBlock.setAttribute('style', block.getAttribute('style') as string);
            }
            segmentNodes.forEach((node) => newBlock.appendChild(node));
            block.parentNode?.insertBefore(newBlock, block);
        });
        block.parentNode?.removeChild(block);
    });

    return container.innerHTML;
};

export function restoreRichEditorStateFromHtml(
    styles: { [index: string]: React.CSSProperties },
    html: string
): EditorState {
    const contentState = convertFromHTML({
        htmlToStyle: (nodeName, node, currentStyle) => {
            if (nodeName === 'del') {
                return currentStyle.add('STRIKETHROUGH');
            }

            if (nodeName === 'span' && !!node.style.fontSize) {
                return currentStyle.add(`FONTSIZE_${parseInt(node.style.fontSize)}`);
            }

            if (nodeName === 'span' && !!node.style.color) {
                let color = '';
                for (const [key, value] of Object.entries(styles)) {
                    if (key.includes('COLOR_') && convertToRGB(value.color) === node.style.color) {
                        color = key;
                    }
                }
                if (color !== '') {
                    return currentStyle.add(color);
                } else {
                    return currentStyle;
                }
            } else {
                return currentStyle;
            }
        },
        // htmlToStyle: (nodeName, node, currentStyle) => {
        //     console.log('nodeName : ', nodeName);
        //     console.log('node : ', node);
        //     console.log('currentStyle : ', currentStyle);
        //     // if (nodeName === 'span' && !!node.style.color) {
        //     //     for (const [key, value] of Object.entries(styleMap)) {
        //     //         console.log('key : ', key);
        //     //         console.log('value : ', value);
        //     //         if (key.includes('COLOR_') && value === node.style.color){
        //     //             return currentStyle.add(key);
        //     //         }
        //     //     }
        //     // }
        // },
        htmlToEntity: (nodeName, node, createEntity) => {
            if (nodeName === 'a') {
                const entityConfig = {};
                entityConfig.link = node.href ? node.getAttribute('href') || node.href : node.href;
                const doc = new DOMParser().parseFromString(node.textContent, "text/html");
                // return doc.documentElement.textContent;
                // console.log('node.innerHTML : ', node.innerHTML);
                // console.log('node.textContent : ', doc.documentElement.textContent);
                entityConfig.text = doc.documentElement.textContent;
                node.textContent = doc.documentElement.textContent;
                return createEntity(
                    'LINK',
                    'MUTABLE',
                    entityConfig,
                );
            }
            if (nodeName === 'img') {
                const entityConfig: {
                    marginRight?: string;
                    marginLeft?: string;
                    display?: string;
                    float?: string; src?: string; alt?: string; height?: string; width?: string; alignment?: string
                } = {};

                entityConfig.src = node.getAttribute ? node.getAttribute('src') || node.src : node.src;
                entityConfig.alt = node.alt;

                const parsedStyles = parseStyleString(node.getAttribute('style'));

                const allowedCssProps = [
                    'width', 'height', 'float', 'display', 'clear',
                    'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
                ];

                Object.entries(parsedStyles).forEach(([cssProp, value]) => {
                    if (allowedCssProps.includes(cssProp)) {
                        const jsPropName = kebabToCamelCase(cssProp);
                        if (jsPropName === 'height' || jsPropName === 'width') {
                            entityConfig[jsPropName] = Number(value.replace('%', ''));
                        } else {
                            entityConfig[jsPropName] = value;
                        }
                    }
                });

                if (entityConfig.float === 'left') {
                    entityConfig.alignment = 'left';
                } else if (entityConfig.float === 'right') {
                    entityConfig.alignment = 'right';
                } else if (
                    entityConfig.display === 'block' &&
                    entityConfig.marginLeft === 'auto' &&
                    entityConfig.marginRight === 'auto'
                ) {
                    entityConfig.alignment = 'center';
                }

                return createEntity(
                    'IMAGE',
                    'MUTABLE',
                    entityConfig,
                );
            }
        },
        htmlToBlock: (nodeName, node) => {
            if (nodeName === 'p' && !!node.style.textAlign) {
                return {
                    type: `align-${node.style.textAlign}`,
                    data: {}
                };
            }
            if (nodeName === 'img') {
                return {
                    type: `atomic`,
                    data: {
                        alignment: `align-${node.parentElement?.style?.textAlign ?? 'left'}`,
                    }
                };
            }
            if (nodeName === 'blockquote') {
                return {
                    type: 'blockquote',
                    data: {}
                };
            }
        }
    })(removeStandaloneNbspFromEmptyParagraphs(splitConsecutiveLineBreaksIntoParagraphs(html)));
    return EditorState.createWithContent(contentState, decorator);
}